import { FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { searchApi } from "../api/searchApi";
import type { SearchFilters, SearchResult } from "../types/filters";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CustomButton from "../components/CustomButton";

type SearchMode = "semantic" | "fulltext";

export function SearchPage() {
  const { projectId = "", repoId = "" } = useParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<SearchMode>("semantic");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  // Optional filters
  const [languageFilter, setLanguageFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");

  async function handleSearch(e: FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setSearched(true);

    const filters: SearchFilters = {};
    if (languageFilter) filters.language = languageFilter;
    if (kindFilter) filters.kind = kindFilter;

    try {
      const res =
        mode === "semantic"
          ? await searchApi.semantic(repoId, query, filters, projectId)
          : await searchApi.fulltext(repoId, query, filters, projectId);
      setResults(res);
    } catch (err) {
      setError((err as Error).message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  function handleResultClick(result: SearchResult) {
    navigate(
      `/projects/${projectId}/repos/${repoId}/explore?symbol=${encodeURIComponent(result.fq_name)}`
    );
  }

  function formatScore(score: number): string {
    return `${Math.round(score * 100)}%`;
  }

  return (
    <div className="content">
      <Fade in timeout={400}>
        <Card className="orgs-create-card" sx={{ mb: 3 }}>
          <CardContent component="form" onSubmit={handleSearch} sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 1.5 }}>
              <input
                style={{ flex: 1, padding: "10px 14px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border-light)", fontSize: 14 }}
                placeholder="Search symbols..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search query"
              />
              <Box className="view-toggle">
                <button
                  type="button"
                  className={`view-toggle-btn ${mode === "semantic" ? "active" : ""}`}
                  onClick={() => setMode("semantic")}
                >
                  Semantic
                </button>
                <button
                  type="button"
                  className={`view-toggle-btn ${mode === "fulltext" ? "active" : ""}`}
                  onClick={() => setMode("fulltext")}
                >
                  Full-text
                </button>
              </Box>
              <CustomButton
                loading={loading}
                disabled={!query.trim()}
                buttonText="Search"
                buttonTextLoading="…"
                handleSubmitClick={() => {}}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <label className="row" style={{ gap: 8 }}>
                <Typography variant="caption" sx={{ color: "var(--brand-muted)", fontWeight: 500 }}>Language</Typography>
                <input
                  placeholder="e.g. python"
                  value={languageFilter}
                  onChange={(e) => setLanguageFilter(e.target.value)}
                  style={{ width: 120, padding: "6px 10px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border-light)", fontSize: 13 }}
                  aria-label="Language filter"
                />
              </label>
              <label className="row" style={{ gap: 8 }}>
                <Typography variant="caption" sx={{ color: "var(--brand-muted)", fontWeight: 500 }}>Kind</Typography>
                <select
                  value={kindFilter}
                  onChange={(e) => setKindFilter(e.target.value)}
                  aria-label="Kind filter"
                  style={{ padding: "6px 10px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border-light)", fontSize: 13 }}
                >
                  <option value="">All kinds</option>
                  <option value="Function">Function</option>
                  <option value="Class">Class</option>
                  <option value="Method">Method</option>
                  <option value="Module">Module</option>
                  <option value="Interface">Interface</option>
                  <option value="Enum">Enum</option>
                  <option value="Type">Type</option>
                  <option value="Route">Route</option>
                  <option value="Resource">Resource</option>
                  <option value="File">File</option>
                </select>
              </label>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {error && (
        <Fade in timeout={200}>
          <Box sx={{ mb: 2 }}>
            <span className="pill danger">{error}</span>
          </Box>
        </Fade>
      )}

      {searched && !loading && results.length === 0 && !error && (
        <Fade in timeout={400}>
          <Card className="orgs-create-card">
            <CardContent>
              <Box className="empty-state">
                <Box className="empty-state-icon">⌕</Box>
                <Typography className="empty-state-desc">No matches found. Try a different query or adjust filters.</Typography>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      )}

      {results.length > 0 && (
        <Fade in timeout={500}>
          <Card className="orgs-create-card">
            <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className="projects-th">Name</TableCell>
                      <TableCell className="projects-th">Kind</TableCell>
                      <TableCell className="projects-th">File</TableCell>
                      <TableCell className="projects-th">Language</TableCell>
                      <TableCell className="projects-th">Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((r, index) => (
                      <TableRow
                        key={r.id}
                        className="projects-row stagger-item"
                        style={{ cursor: "pointer", animationDelay: `${index * 0.03}s` }}
                        onClick={() => handleResultClick(r)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") handleResultClick(r);
                        }}
                      >
                        <TableCell className="projects-td">
                          <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleResultClick(r); }}
                            style={{ color: "var(--brand-pink)", fontWeight: 500 }}>
                            {r.name}
                          </a>
                        </TableCell>
                        <TableCell className="projects-td">
                          <Chip label={r.kind} size="small" className="explorer-chip" />
                        </TableCell>
                        <TableCell className="projects-td" sx={{ fontFamily: "monospace", fontSize: "12px !important", color: "var(--brand-muted) !important" }}>
                          {r.file_path}
                        </TableCell>
                        <TableCell className="projects-td">{r.language ?? <span className="muted">—</span>}</TableCell>
                        <TableCell className="projects-td">
                          <Chip label={formatScore(r.score)} size="small"
                            sx={{ fontSize: 11, fontWeight: 600, backgroundColor: "rgba(52, 199, 89, 0.1)", color: "#22c55e" }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Fade>
      )}
    </div>
  );
}
